// ============================================================
// HomeHeaderBottomTabs.jsx
//
// Main tabbed navigation component for the home page.
// Houses the four primary content sections:
//   0 — Services    (SoftwareService)
//   1 — Projects    (IoTService)
//   2 — AI Adviser  (AiCourseAssistancePage)
//   3 — Courses     (TechStack)
//
// Features:
//   - Animated tab content transitions via framer-motion
//   - Loading skeleton shown during tab switch (600ms)
//   - NewsUpdate ticker above the tab bar
//   - Fully responsive with brand dark navy + gold theme
// ============================================================

import * as React from "react";
import {
    Tabs, Tab, Box, styled,
    Skeleton, Stack, Typography,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";

// ── MUI Icons ────────────────────────────────────────────────
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import SchoolIcon from "@mui/icons-material/School";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

// ── Internal Components ──────────────────────────────────────
import SoftwareService from "./SoftwareService";
import IoTService from "./IoTService";
import TechStack from "./TechStack";
import NewsUpdate from "./NewsUpdate";
import AiCourseAssistancePage from "./AiCourseAssistancePage";

// ============================================================
//  CONSTANTS
// ============================================================

/**
 * Brand colors — consistent with the rest of the platform.
 */
const BRAND = {
    dark:  "#100720",
    gold:  "#E5C95F",
    white: "#FFFFFF",
};

/**
 * TAB_CONFIG
 * Defines the label, icon, and component for each tab.
 * To add a new tab, add an entry here — no other changes needed.
 */
const TAB_CONFIG = [

    {
        label:     "AI Adviser",
        icon:      <AutoAwesomeIcon />,
        component: <AiCourseAssistancePage />,
    },
    {
        label:     "Services",
        icon:      <BusinessCenterIcon />,
        component: <SoftwareService />,
    },
    {
        label:     "Projects",
        icon:      <RocketLaunchIcon />,
        component: <IoTService />,
    },

    {
        label:     "Courses",
        icon:      <SchoolIcon />,
        component: <TechStack />,
    },

];

/**
 * SKELETON_DELAY_MS
 * How long the loading skeleton is shown after a tab switch.
 * Gives a smooth transition feel without jarring content jumps.
 */
const SKELETON_DELAY_MS = 600;

// ============================================================
//  STYLED COMPONENTS
// ============================================================

/**
 * TabsContainer
 * Dark branded bar that holds the NewsUpdate ticker
 * and the tab navigation row.
 */
const TabsContainer = styled(Box)({
    backgroundColor: BRAND.dark,
    width: "100%",
    padding: "10px 0",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
});

/**
 * StyledTab
 * Individual tab button with gold active/hover states
 * and a subtle icon lift animation on hover.
 */
const StyledTab = styled((props) => <Tab disableRipple {...props} />)(() => ({
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.95rem",
    fontFamily: "'Poppins', sans-serif",
    color: "rgba(255,255,255,0.6)",
    transition: "color 0.3s ease",

    // Active tab — gold text
    "&.Mui-selected": {
        color: BRAND.gold,
    },

    // Hover — gold text
    "&:hover": {
        color: BRAND.gold,
        opacity: 1,
    },

    // Icon animation on hover
    "& .MuiSvgIcon-root": {
        transition: "transform 0.3s ease",
    },
    "&:hover .MuiSvgIcon-root": {
        transform: "translateY(-3px)",
    },
}));

// ============================================================
//  SUB COMPONENTS
// ============================================================

/**
 * TabSkeleton
 * Placeholder content shown while the new tab loads.
 * Matches the rough shape of the actual tab content
 * to prevent layout shift.
 */
const TabSkeleton = () => (
    <Stack spacing={2} sx={{ alignItems: "center", mt: 4, px: 2 }}>
        {/* Simulates a section headline */}
        <Skeleton
            variant="text"
            width="40%"
            height={60}
            sx={{ bgcolor: "rgba(0,0,0,0.05)", borderRadius: 2 }}
        />
        {/* Simulates a tagline badge */}
        <Skeleton
            variant="rounded"
            width="60%"
            height={50}
            sx={{ borderRadius: "50px", bgcolor: "rgba(0,0,0,0.05)" }}
        />
        {/* Simulates main content block */}
        <Skeleton
            variant="rectangular"
            width="100%"
            height={300}
            sx={{ borderRadius: 4, bgcolor: "rgba(0,0,0,0.05)" }}
        />
    </Stack>
);

// ============================================================
//  ANIMATION VARIANTS
// ============================================================

/**
 * contentVariants
 * Framer Motion animation config for tab content transitions.
 * Content fades in from slightly below and exits upward.
 */
const contentVariants = {
    initial: { opacity: 0, y: 15 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
    exit:    { opacity: 0, y: -15, transition: { duration: 0.2 } },
};

const skeletonVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { duration: 0.2 } },
    exit:    { opacity: 0, transition: { duration: 0.15 } },
};

// ============================================================
//  MAIN COMPONENT
// ============================================================

export default function HomeHeaderBottomTabs() {

    // ── State ────────────────────────────────────────────────
    // activeTab — index of the currently selected tab
    const [activeTab, setActiveTab] = React.useState(0);

    // isSyncing — true during the 600ms skeleton display
    // period after a tab switch
    const [isSyncing, setIsSyncing] = React.useState(false);

    // ============================================================
    //  EFFECTS
    // ============================================================

    /**
     * Show skeleton for SKELETON_DELAY_MS after every tab change.
     * Cleans up the timer if the component unmounts early.
     */
    React.useEffect(() => {
        setIsSyncing(true);
        const timer = setTimeout(() => setIsSyncing(false), SKELETON_DELAY_MS);
        return () => clearTimeout(timer);
    }, [activeTab]);

    // ============================================================
    //  HANDLERS
    // ============================================================

    /**
     * handleTabChange
     * Updates the active tab index when user clicks a tab.
     */
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    // ============================================================
    //  RENDER
    // ============================================================

    return (
        <Box sx={{ width: "100%", bgcolor: "#E5C95F" }}>

            {/* ── Dark Tab Navigation Bar ── */}
            <TabsContainer>

                {/* News ticker above tabs */}
                <NewsUpdate />

                {/* Tab buttons */}
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    centered
                    TabIndicatorProps={{
                        style: {
                            backgroundColor: BRAND.gold,
                            height: "3px",
                            borderRadius: "2px",
                        },
                    }}
                    sx={{
                        // Scrollable on small screens instead of wrapping
                        "& .MuiTabs-flexContainer": {
                            flexWrap: { xs: "nowrap" },
                        },
                        overflowX: "auto",
                    }}
                >
                    {/* Render tabs dynamically from TAB_CONFIG */}
                    {TAB_CONFIG.map((tab) => (
                        <StyledTab
                            key={tab.label}
                            icon={tab.icon}
                            iconPosition="start"
                            label={tab.label}
                        />
                    ))}
                </Tabs>
            </TabsContainer>

            {/* ── Animated Tab Content ── */}
            <Box
                sx={{
                    maxWidth: "1200px",
                    margin: "0 auto",
                    overflow: "hidden",
                    p: { xs: 2, md: 4 },
                }}
            >
                <AnimatePresence mode="wait">
                    {isSyncing ? (
                        // ── Loading skeleton during tab switch ──
                        <motion.div
                            key="skeleton"
                            variants={skeletonVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                        >
                            <TabSkeleton />
                        </motion.div>
                    ) : (
                        // ── Active tab content ──
                        <motion.div
                            key={activeTab}
                            variants={contentVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                        >
                            <Box sx={{ minHeight: "60vh" }}>
                                {TAB_CONFIG[activeTab].component}
                            </Box>
                        </motion.div>
                    )}
                </AnimatePresence>
            </Box>

        </Box>
    );
}
